import React, { MouseEvent } from 'react';
import IForm from '../../../../types/IForm';
import { MoveContentBlockForm } from '../forms/MoveContentBlockForm/MoveContentBlockForm';
import {
  actionKeyToIcon,
  getIndex,
  getPosition,
  isActionDisabled,
} from '../../../blocks/ContentWrapper/ContentWrapper.utils';
import { Namespace } from '../../../../translation/namespace';
import Btn, { BtnModifier } from '../../../molecules/Btn';
import { TooltipPlacement } from '../../../atoms/Tooltip';
import { BlockType } from '../../../../types/api/ElementType';
import { ActionState, ErrorState } from '../../../blocks/ContentWrapper/useContentWrapper';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { sendLiveRegionMessage } from '../../../atoms/LiveRegionGlobal/LiveRegionGlobal';
import { AddContentBlockForm } from '../forms/AddContentBlockForm/AddContentBlockForm';
import {
  ContentWrapperAction as Action,
  ContentWrapperBlocks,
  ContentWrapperBlockTypes,
} from '../../../blocks/ContentWrapper/ContentWrapper.types';
import { ToggleActiveView } from '../BlockInteractionsTypes';

type DeleteBlockAction = {
  actionType: Action.deleteBlock;
  elementType: Exclude<ContentWrapperBlockTypes, BlockType.layoutgroup>;
};

type ConnectBlocksAction = {
  actionType: Action.connectBlocks;
  elementType: BlockType.contentimage | BlockType.layoutgroup;
};

type MoveBlockAction = {
  actionType: Action.moveBlockUp | Action.moveBlockDown;
  elementType: ContentWrapperBlockTypes;
};
type AddBlockAction = {
  actionType: Action.addBlock;
  elementType: ContentWrapperBlockTypes;
};

type BlockAction = DeleteBlockAction | ConnectBlocksAction | MoveBlockAction | AddBlockAction;

type InteractionButtonProps = BlockAction & {
  /** UUID of rendered Block */
  uuid: string;
  form: IForm;
  toggleActiveView: ToggleActiveView;
  isInLayoutGroup: boolean;

  /** Blocks' index inside the ContentWrapper */
  index: number;
  /** The Connect Action shall only be enabled on a contentimage block followed by a richtext block */
  nextBlockIsRichtext: boolean;
  /** Number of Blocks the ContentWrapper handles */
  numBlocks: number;
  onMovingFailure: (uuid: string) => void;
  onMovingSuccess: (actionType: string, uuid: string, elementType: string) => void;
  reference?: React.RefObject<HTMLButtonElement>;
  setAction: (action: ActionState) => void;
  addBlock: (newBlock: ContentWrapperBlocks, position: number) => void;
  setError: React.Dispatch<React.SetStateAction<ErrorState | null>>;
};

/** Renders ContentWrapper actions */
export function InteractionButton({
  actionType,
  elementType,
  form,
  index,
  isInLayoutGroup,
  nextBlockIsRichtext,
  numBlocks,
  onMovingFailure,
  onMovingSuccess,
  reference,
  setAction,
  addBlock,
  setError,
  toggleActiveView,
  uuid,
}: InteractionButtonProps) {
  const { t } = useSonTranslation(Namespace.ContentWrapper);

  const onClick = (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    toggleActiveView(event, actionType);
  };

  switch (actionType) {
    case Action.moveBlockUp:
    case Action.moveBlockDown:
      return (
        <MoveContentBlockForm
          key={`contentwrapper-${actionType}`}
          elementType={elementType}
          blockIndex={index}
          form={form}
          actionKey={actionType}
          isActionDisabled={isActionDisabled(actionType, index, numBlocks, elementType, isInLayoutGroup)}
          actionKeyToIcon={actionKeyToIcon[actionType]}
          beforeSubmit={() => {
            setAction({ uuid, type: actionType });
            sendLiveRegionMessage(t(`moveInProgress.${elementType}`));
          }}
          onSuccess={() => onMovingSuccess(actionType, uuid, elementType)}
          onFailure={() => onMovingFailure(uuid)}
        />
      );
    case Action.deleteBlock:
      return (
        <Btn
          reference={reference}
          key={`${actionType}-${uuid}`}
          data-id={actionType}
          text={t(`actions.${actionType}.text.${elementType}`, { position: getIndex(index) })}
          titleText={t(`actions.${actionType}.titleText.${elementType}`, { position: getIndex(index) })}
          hiddenText
          modifier={[BtnModifier.MINOR_ACTION, BtnModifier.TRANSPARENT]}
          svgSpriteId={actionKeyToIcon[actionType]}
          ariaDisabled={isActionDisabled(actionType, index, numBlocks, elementType, isInLayoutGroup)}
          // aria-controls and expanded is not needed when type is modal
          routing={false}
          tooltipPlacement={TooltipPlacement.TOP}
          onClick={onClick}
        />
      );
    case Action.connectBlocks:
      return (
        <Btn
          reference={reference}
          key={`${actionType}-${uuid}`}
          data-id={actionType}
          text={t(
            `actions.${actionType}.text.${elementType}`,
            getPosition(index, undefined, elementType, false, actionType),
          )}
          titleText={t(
            `actions.${actionType}.titleText.${elementType}`,
            getPosition(index, undefined, elementType, false, actionType),
          )}
          hiddenText
          modifier={[BtnModifier.MINOR_ACTION, BtnModifier.TRANSPARENT]}
          svgSpriteId={actionKeyToIcon[actionType]}
          ariaDisabled={
            (elementType !== BlockType.layoutgroup && nextBlockIsRichtext) ||
            isActionDisabled(actionType, index, numBlocks, elementType, isInLayoutGroup)
          }
          // aria-controls and expanded is not needed when type is modal
          routing={false}
          tooltipPlacement={TooltipPlacement.TOP}
          onClick={onClick}
        />
      );
    case Action.addBlock:
      return (
        <AddContentBlockForm
          uuid={uuid}
          form={form}
          addBlock={addBlock}
          setAction={setAction}
          setError={setError}
          // index starts from zero, so we have to add one
          position={index + 1}
        />
      );
    default:
      return null;
  }
}
